import { MGPOptional, Set, MGPValidation } from '@everyboard/lib';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord } from '../../jscaip/Coord';
import { CoordSet } from '../../jscaip/CoordSet';
import { GameStatus } from '../../jscaip/GameStatus';
import { HexagonalUtils } from '../../jscaip/HexagonalUtils';
import { Player } from '../../jscaip/Player';
import { PlayerNumberMap } from '../../jscaip/PlayerMap';
import { Rules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { HiveFailure } from './HiveFailure';
import { HiveDropMove, HiveCoordToCoordMove } from './HiveMove';
import { HivePieceRules } from './HivePieceRules';
import { HiveState } from './HiveState';
export class HiveNode extends GameNode {
}
export class HiveRules extends Rules {
    static singleton = MGPOptional.empty();
    static get() {
        if (HiveRules.singleton.isAbsent()) {
            HiveRules.singleton = MGPOptional.of(new HiveRules());
        }
        return HiveRules.singleton.get();
    }
    getInitialState() {
        const board = [];
        return HiveState.fromRepresentation(board, 0);
    }
    applyLegalMove(move, state, _config, _info) {
        if (move instanceof HiveDropMove) {
            return this.applyLegalDrop(move, state);
        }
        else if (move instanceof HiveCoordToCoordMove) {
            return this.applyLegalMoveCoordToCoord(move, state);
        }
        else {
            // Move is pass
            return state.update().increaseTurnAndFinalizeUpdate();
        }
    }
    applyLegalDrop(drop, state) {
        // Put the piece where it is dropped, possibly on top of other pieces
        const pieceStack = state.getAt(drop.coord);
        return state.update()
            .setAt(drop.coord, pieceStack.add(drop.piece))
            .removeRemainingPiece(drop.piece)
            .increaseTurnAndFinalizeUpdate();
    }
    applyLegalMoveCoordToCoord(move, state) {
        // Take the top piece of the source and move it on top of the destination
        const sourcePieceStack = state.getAt(move.getStart());
        const destinationPieceStack = state.getAt(move.getEnd());
        return state.update()
            .setAt(move.getStart(), sourcePieceStack.removeTopPiece())
            .setAt(move.getEnd(), destinationPieceStack.add(sourcePieceStack.topPiece()))
            .increaseTurnAndFinalizeUpdate();
    }
    isLegal(move, state) {
        if (move instanceof HiveDropMove) {
            return this.isLegalDrop(move, state);
        }
        else if (move instanceof HiveCoordToCoordMove) {
            return this.isLegalMoveCoordToCoord(move, state);
        }
        else {
            // Move is pass
            if (this.shouldPass(state)) {
                return MGPValidation.SUCCESS;
            }
            else {
                return MGPValidation.failure(RulesFailure.CANNOT_PASS());
            }
        }
    }
    isLegalMoveCoordToCoord(move, state) {
        if (state.queenBeeLocation(state.getCurrentPlayer()).isPresent() === false) {
            return MGPValidation.failure(HiveFailure.QUEEN_BEE_MUST_BE_ON_BOARD_BEFORE_MOVE());
        }
        const stack = state.getAt(move.getStart());
        if (stack.isEmpty()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        const movedPiece = stack.topPiece();
        if (movedPiece.owner === state.getCurrentOpponent()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        const moveValidity = HivePieceRules.of(movedPiece).moveValidity(move, state);
        if (moveValidity.isFailure()) {
            return moveValidity;
        }
        const stateWithoutMovedPiece = state.update()
            .setAt(move.getStart(), stack.removeTopPiece())
            .increaseTurnAndFinalizeUpdate();
        if (stateWithoutMovedPiece.isDisconnected()) {
            // Cannot disconnect the hive, even in the middle of a move
            return MGPValidation.failure(HiveFailure.CANNOT_DISCONNECT_HIVE());
        }
        const newEnd = move.getEnd();
        if (stateWithoutMovedPiece.numberOfNeighbors(newEnd) === 0) {
            // Cannot disconnect the hive after the move, i.e., the destination should have one occupied neighbor
            return MGPValidation.failure(HiveFailure.CANNOT_DISCONNECT_HIVE());
        }
        return MGPValidation.SUCCESS;
    }
    mustPlaceQueenBee(state) {
        return 6 <= state.turn && state.hasQueenBeeOnBoard(state.getCurrentPlayer()) === false;
    }
    isLegalDrop(move, state) {
        const player = state.getCurrentPlayer();
        // This should be a piece of the player
        if (move.piece.owner === player.getOpponent()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        // The player must have the piece in its reserve to drop it
        if (state.remainingPieces.hasRemaining(move.piece) === false) {
            return MGPValidation.failure(HiveFailure.CANNOT_DROP_PIECE_YOU_DONT_HAVE());
        }
        // The queen bee must be placed at the latest at the fourth turn of a player
        if (move.piece.kind !== 'QueenBee' && this.mustPlaceQueenBee(state)) {
            return MGPValidation.failure(HiveFailure.MUST_PLACE_QUEEN_BEE_LATEST_AT_FOURTH_TURN());
        }
        // Pieces must be connected to the hive, except at the first turn of player zero,
        // but must not be dropped next to a piece of the opponent, except at the first turn of player one
        const neighborValidity = this.checkNeighborValidity(move, state);
        if (neighborValidity.isFailure()) {
            return neighborValidity;
        }
        // Pieces must be dropped on an empty space (even the beetle)
        if (state.getAt(move.coord).hasPieces()) {
            return MGPValidation.failure(HiveFailure.MUST_DROP_ON_EMPTY_SPACE());
        }
        return MGPValidation.SUCCESS;
    }
    checkNeighborValidity(move, state) {
        const player = state.getCurrentPlayer();
        let hasNeighbor = false;
        for (const neighbor of HexagonalUtils.getNeighbors(move.coord)) {
            const neighborStack = state.getAt(neighbor);
            if (neighborStack.hasPieces()) {
                hasNeighbor = true;
                if (state.turn !== 1 && neighborStack.topPiece().owner === player.getOpponent()) {
                    return MGPValidation.failure(HiveFailure.CANNOT_DROP_NEXT_TO_OPPONENT());
                }
            }
        }
        if (state.turn !== 0 && hasNeighbor === false) {
            return MGPValidation.failure(HiveFailure.MUST_BE_CONNECTED_TO_HIVE());
        }
        return MGPValidation.SUCCESS;
    }
    getPossibleDropLocations(state) {
        const player = state.getCurrentPlayer();
        // At turn 0 and 1, the possible drop locations are already known
        if (state.turn === 0) {
            return new CoordSet([new Coord(0, 0)]);
        }
        if (state.turn === 1) {
            return new CoordSet(HexagonalUtils.getNeighbors(new Coord(0, 0)));
        }
        const remainingPieceOpt = state.remainingPieces.getAny(player);
        if (remainingPieceOpt.isAbsent()) {
            return new CoordSet();
        }
        const remainingPiece = remainingPieceOpt.get();
        let locations = new CoordSet();
        // We can only drop next to one of our piece
        for (const coord of state.occupiedSpaces()) {
            if (state.getAt(coord).topPiece().owner === player) {
                for (const neighbor of state.emptyNeighbors(coord)) {
                    const move = HiveDropMove.of(remainingPiece, neighbor);
                    if (this.isLegalDrop(move, state).isSuccess()) {
                        locations = locations.addElement(neighbor);
                    }
                }
            }
        }
        return locations;
    }
    getPossibleMovesFrom(state, coord) {
        const player = state.getCurrentPlayer();
        let moves = new Set();
        const topPiece = state.getAt(coord).topPiece();
        if (topPiece.owner === player) {
            for (const move of HivePieceRules.of(topPiece).getPotentialMoves(coord, state)) {
                if (this.isLegalMoveCoordToCoord(move, state).isSuccess()) {
                    moves = moves.addElement(move);
                }
            }
        }
        return moves;
    }
    getPossibleMovesOnBoard(state) {
        let moves = new Set();
        for (const coord of state.occupiedSpaces()) {
            moves = moves.union(this.getPossibleMovesFrom(state, coord));
        }
        return moves;
    }
    shouldPass(state) {
        return this.getPossibleDropLocations(state).size() === 0 && this.getPossibleMovesOnBoard(state).size() === 0;
    }
    getGameStatus(node) {
        const state = node.gameState;
        const neighbors = PlayerNumberMap.of(0, 0);
        for (const player of Player.PLAYERS) {
            const queenBeeLocation = state.queenBeeLocation(player);
            if (queenBeeLocation.isPresent()) {
                neighbors.put(player, state.numberOfNeighbors(queenBeeLocation.get()));
            }
        }
        const neighborsZero = neighbors.get(Player.ZERO);
        const neighborsOne = neighbors.get(Player.ONE);
        if (neighborsZero === 6 && neighborsOne === 6) {
            return GameStatus.DRAW;
        }
        else if (neighborsZero === 6) {
            return GameStatus.getVictory(Player.ONE);
        }
        else if (neighborsOne === 6) {
            return GameStatus.getVictory(Player.ZERO);
        }
        return GameStatus.ONGOING;
    }
}
